import css from '@styled-system/css'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import React, { useState } from 'react'
import YouTube, { YouTubeProps } from 'react-youtube'
import PlayIcon from '../assets/images/icons/play.svg'
import Box from './primitives/Box'

export type Props = {
  thumbnail?: IGatsbyImageData
  videoId: string
  autoplay?: boolean
  width?: number | string
  borderRadius?: number
  showCaptions?: boolean
}

const videoWidth = 1280
const videoHeight = 720

export const YoutubeVideo = ({
  thumbnail,
  videoId,
  autoplay = false,
  width = 1280,
  borderRadius = 24,
  showCaptions = false,
}: Props) => {
  const [isPlaying, setIsPlaying] = useState(autoplay)
  const [videoPlayer, setVideoPlayer] = useState<{ playVideo: () => null }>()
  const videoOpts: YouTubeProps['opts'] = {
    width: videoWidth,
    height: videoHeight,
    playerVars: {
      rel: 0,
      modestbranding: 1,
      ...(showCaptions ? { cc_load_policy: 1 } : {}),
    },
    host: 'https://www.youtube-nocookie.com/',
  }

  const playVideo = () => {
    setIsPlaying(true)
    if (videoPlayer) {
      videoPlayer.playVideo()
    }
  }

  const onReady = event => {
    setVideoPlayer(event.target)
    if (isPlaying) {
      event.target.playVideo()
    }
  }

  return (
    <Box
      marginX="auto"
      display="flex"
      justifyContent="center"
      position="relative"
      //@ts-expect-error
      width={width}
      maxWidth="100%"
      css={css({
        aspectRatio: `${videoWidth} / ${videoHeight}`,
        '.videoWrapper': {
          width: '100%',
          height: '100%',
        },
        iframe: {
          background: 'black',
          width: '100%',
          height: '100%',
          borderRadius,
        },
      })}
    >
      <YouTube
        className="videoWrapper"
        videoId={videoId}
        opts={videoOpts}
        onReady={onReady}
      />
      {!isPlaying && thumbnail && (
        <Thumbnail onPlayClick={playVideo} thumbnail={thumbnail} />
      )}
    </Box>
  )
}

interface ThumbnailProps {
  onPlayClick: () => void
  thumbnail: IGatsbyImageData
}
const Thumbnail = (props: ThumbnailProps) => {
  const { onPlayClick, thumbnail } = props
  return (
    <Box.button
      position="absolute"
      marginX="auto"
      borderRadius={24}
      isolation="isolate"
      display="flex"
      justifyContent="center"
      alignItems="center"
      padding={0}
      css={css({
        height: 'auto',
        ':hover': {
          transform: 'none',
        },
        ':hover .play': {
          transform: 'scale3d(1.1,1.1,1.1)',
        },
        '[data-gatsby-image-wrapper] img': {
          borderRadius: 24,
        },
        // Disable animation
        '[data-placeholder-image]': {
          display: 'none',
        },
      })}
      onClick={onPlayClick}
      aria-label="Play video"
    >
      <Box.img
        loading="lazy"
        className="play"
        zIndex={50}
        src={PlayIcon}
        position="absolute"
        transition="All 0.15s ease"
        css={`
          filter: drop-shadow(0px 13px 45px rgba(0, 0, 0, 0.1))
            drop-shadow(0px 17px 45px rgba(23, 23, 26, 0.12));
        `}
      />
      <GatsbyImage image={thumbnail} alt="" />
    </Box.button>
  )
}
